import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import type { Locale } from "antd/es/locale";
import elGR from "antd/locale/el_GR";
import enUS from "antd/locale/en_US";
import dayjs from "dayjs";

import logo from "./assets/hrtlogo.png";
import irisLogo from "./assets/iris.png";

const { Title, Text } = Typography;

const API_BASE = import.meta.env.VITE_API_BASE_URL;

type Lang = "en" | "el";

const DICT = {
  intro: {
    en: "Enter your passport number and date of birth to see your account.",
    el: "Εισάγετε τον αριθμό διαβατηρίου και την ημερομηνία γέννησής σας για να δείτε τον λογαριασμό σας.",
  },
  passport: { en: "Passport number", el: "Αριθμός διαβατηρίου" },
  dob: { en: "Date of birth", el: "Ημερομηνία γέννησης" },
  submit: { en: "View my rent", el: "Προβολή ενοικίου" },
  not_found: {
    en: "No records found. Check your details or contact the office.",
    el: "Δεν βρέθηκαν εγγραφές. Ελέγξτε τα στοιχεία σας ή επικοινωνήστε με το γραφείο.",
  },
  error: {
    en: "Something went wrong. Please try again.",
    el: "Κάτι πήγε στραβά. Δοκιμάστε ξανά.",
  },
  year: { en: "Year", el: "Έτος" },
  month: { en: "Month", el: "Μήνας" },
  day: { en: "Day", el: "Ημέρα" },
  pay: { en: "Pay", el: "Πληρωμή" },
  paid: { en: "Paid", el: "Πληρώθηκε" },
  unpaid: { en: "Unpaid", el: "Απλήρωτο" },
  no_charges: {
    en: "No rent charges yet.",
    el: "Δεν υπάρχουν χρεώσεις ενοικίου ακόμη.",
  },
  refresh: { en: "Refresh", el: "Ανανέωση" },
  logout: { en: "Log out", el: "Αποσύνδεση" },
  iris_hint: {
    en: "When paying your rent, please choose IRIS on the payment page.",
    el: "Όταν πληρώνετε το ενοίκιό σας, επιλέξτε IRIS στη σελίδα πληρωμής.",
  },
  paid_hint: {
    en: "After paying, tap Refresh to see the payment. It can take a few minutes to appear.",
    el: "Αφού πληρώσετε, πατήστε Ανανέωση για να δείτε την πληρωμή. Μπορεί να χρειαστούν λίγα λεπτά για να εμφανιστεί.",
  },
} as const;

const MONTHS: Record<Lang, string[]> = {
  en: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ],
  el: [
    "Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος",
    "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος",
  ],
};

function antdLocale(lang: Lang): Locale {
  return lang === "el" ? elGR : enUS;
}

interface Charge {
  period: string;
  amount: number;
  paid_on: string | null;
  payment_link: string | null;
}

interface RentData {
  given_name: string;
  family_name: string;
  charges: Charge[];
}

export function App() {
  const [lang, setLang] = useState<Lang>(
    navigator.language?.toLowerCase().startsWith("el") ? "el" : "en",
  );
  const [passport, setPassport] = useState("");
  const [dobYear, setDobYear] = useState<number | null>(null);
  const [dobMonth, setDobMonth] = useState<number | null>(null);
  const [dobDay, setDobDay] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"not_found" | "error" | null>(null);
  const [data, setData] = useState<RentData | null>(null);

  const t = (key: keyof typeof DICT) => DICT[key][lang];

  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 76 }, (_, i) => thisYear - 15 - i);
  const daysInMonth =
    dobYear && dobMonth ? new Date(dobYear, dobMonth, 0).getDate() : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const dob =
    dobYear && dobMonth && dobDay
      ? `${dobYear}-${String(dobMonth).padStart(2, "0")}-${String(dobDay).padStart(2, "0")}`
      : null;

  const submit = async () => {
    if (!passport.trim() || !dob || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/portal/rent-charges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          passport: passport.trim(),
          dob,
        }),
      });
      if (res.status === 404) {
        setError("not_found");
        return;
      }
      if (!res.ok) {
        setError("error");
        return;
      }
      setData((await res.json()) as RentData);
    } catch {
      setError("error");
    } finally {
      setLoading(false);
    }
  };

  const monthLabel = (period: string) =>
    `${MONTHS[lang][Number(period.slice(5, 7)) - 1]} ${period.slice(0, 4)}`;

  return (
    <ConfigProvider locale={antdLocale(lang)}>
      <div className="wrap">
        <div className="header">
          <div className="brand">
            <img src={logo} alt="HRT" className="logo" />
            <Title level={3} style={{ margin: 0 }}>
              HRT Portal
            </Title>
          </div>
          <Button size="small" onClick={() => setLang(lang === "en" ? "el" : "en")}>
            {lang === "en" ? "ΕΛ" : "EN"}
          </Button>
        </div>

        {!data ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Text type="secondary">{t("intro")}</Text>
              <div>
                <label htmlFor="passport">{t("passport")}</label>
                <Input
                  id="passport"
                  size="large"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  value={passport}
                  onChange={(e) => setPassport(e.target.value)}
                />
              </div>
              <div>
                <label>{t("dob")}</label>
                <div className="dob-row">
                  <Select<number>
                    size="large"
                    placeholder={t("year")}
                    value={dobYear}
                    onChange={(v) => setDobYear(v)}
                    options={years.map((y) => ({ value: y, label: String(y) }))}
                    style={{ flex: 1.2 }}
                  />
                  <Select<number>
                    size="large"
                    placeholder={t("month")}
                    value={dobMonth}
                    onChange={(v) => setDobMonth(v)}
                    options={MONTHS[lang].map((m, i) => ({ value: i + 1, label: m }))}
                    style={{ flex: 1.6 }}
                  />
                  <Select<number>
                    size="large"
                    placeholder={t("day")}
                    value={dobDay && dobDay <= daysInMonth ? dobDay : null}
                    onChange={(v) => setDobDay(v)}
                    options={days.map((d) => ({ value: d, label: String(d) }))}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
              <Button
                type="primary"
                size="large"
                block
                htmlType="submit"
                loading={loading}
                disabled={!passport.trim() || !dob}
              >
                {t("submit")}
              </Button>
              {error && <Alert type="warning" message={t(error)} showIcon />}
            </Space>
          </form>
        ) : (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Alert
              type="info"
              title={
                <div className="iris-banner">
                  <img src={irisLogo} alt="IRIS" className="iris-logo" />
                  <span>{t("iris_hint")}</span>
                </div>
              }
            />
            <Title level={5} style={{ margin: "4px 0" }}>
              {data.given_name} {data.family_name}
            </Title>
            {data.charges.length === 0 ? (
              <Text type="secondary">{t("no_charges")}</Text>
            ) : (
              data.charges.map((c) => (
                <Card key={c.period} size="small">
                  <div className="charge-info">
                    <Text strong>{monthLabel(c.period)}</Text>
                    <Text strong style={{ fontSize: 16 }}>
                      {c.amount.toFixed(2)} €
                    </Text>
                  </div>
                  <div className="charge-status">
                    {c.paid_on ? (
                      <Tag color="green" style={{ margin: 0 }}>
                        {t("paid")} {dayjs(c.paid_on).format("DD/MM/YYYY")}
                      </Tag>
                    ) : (
                      <>
                        <Tag color="orange" style={{ margin: 0 }}>
                          {t("unpaid")}
                        </Tag>
                        {c.payment_link && (
                          <Button
                            type="primary"
                            href={c.payment_link}
                            target="_blank"
                            rel="noopener"
                          >
                            {t("pay")}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              ))
            )}
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("paid_hint")}
            </Text>
            <div className="actions">
              <Button type="primary" size="large" onClick={submit} loading={loading} block>
                {t("refresh")}
              </Button>
              <Button
                size="large"
                onClick={() => {
                  setData(null);
                  setPassport("");
                  setDobYear(null);
                  setDobMonth(null);
                  setDobDay(null);
                  setError(null);
                }}
              >
                {t("logout")}
              </Button>
            </div>
          </Space>
        )}
      </div>
    </ConfigProvider>
  );
}
